import ERRORS from 'utils/errors.utils';
import STATUS from './status';

// //////////////////////////////////////////////////////////////////////////////
// API ERROR

export const ERROR_TYPE = {
    BAD_REQUEST: 'BAD_REQUEST',
    MALFORMED_RESPONSE: 'MALFORMED_RESPONSE',
    TRANSFORMATION: 'TRANSFORMATION',
    UNEXPECTED: 'UNEXPECTED',
};
export function ApiError(this: any, { type = ERROR_TYPE.UNEXPECTED, message = 'Unexpected Error', result }) {
    this.name = 'ApiError';
    this.type = type;
    this.message = `${type}: ${message}`;
    // this.stack = new Error().stack;
    this.stack = '';
    this.result = result;
}
ApiError.prototype = Object.create(Error.prototype);
ApiError.prototype.constructor = ApiError;

// //////////////////////////////////////////////////////////////////////////////
// API REQUEST

/**
 * Return a promise that will perform the request. If something is wrong, it will be rejected with an ApiError
 * @param {promise} request the request (promise)
 * @param {Object} [options] options
 * @param {number|number[]} [options.allow] indicates a status number of a list of them. They will be considered as accepted responses, so the response will be resolved.
 * @param {boolean} [options.rawAxiosResult=false] indicates if the returned result is the original given by apisauce (axios). If true, it will return the data object directly.
 * @returns a function that given any result, it will return a promise rejection with a new ApiError
 * @example
 *      apiRequest(Api().users.list())
 *          .then(users => console.log(users))
 *          .catch(error => console.error(error))
 */

interface ApiRequestProps {
    /**
     * Request promise
     */
    request: any;
    /**
     * Request options
     */
    options?: any;
    /**
     * Request promise
     */
    then?: any;
}
export const apiRequest = (request, options?): ApiRequestProps => {
    const { allow, rawAxiosResult = false } = options || {};
    const normalizedAllow = (Array.isArray(allow) ? allow : [allow]).filter(status => typeof status === 'number');
    return request.then(
        result =>
            result && (result.ok === true || normalizedAllow.includes(result.status))
                ? Promise.resolve(rawAxiosResult ? result : result.data)
                : /* eslint-disable indent */
                  Promise.reject(
                      new ApiError({
                          type: ERROR_TYPE.BAD_REQUEST,
                          message: `${result.problem} in ${result.url}`,
                          result,
                      })
                  )
        /* eslint-enable indent */
    );
};

// //////////////////////////////////////////////////////////////////////////////
// THROWING ERRORS

/**
 * Throws an ApiError
 * @param {string} [message] the message of the error
 * @param {string} [type] the ERROR_TYPE
 * @returns a function that given any result, it will return a promise rejection with a new ApiError
 */
export const throwError = (message, type) => result => Promise.reject(new ApiError({ type, message, result }));

/**
 * Perform an action with the given error and re-throw it again.
 * @param {function} fn the function (let's call it 'action')
 * @returns a function that given any error, it will execute the action and re-throw it again
 */
export const rethrowError = fn => error => {
    fn(error);
    throw error;
};

// //////////////////////////////////////////////////////////////////////////////
// NOTIFICATIONS

interface NotificationProps {
    /**
     * Notification type
     */
    type?: string | any;
    /**
     * Notification send method
     */
    send?(any);
    /**
     * Notification error method
     */
    error?(response);
}

export const notifyActionFactory = ({
    send = () => {},
    error = () => {},
    type,
}: NotificationProps) => notification => async (result?) => {
    if (send) {
        await send({ ...notification, type });
    }
    if (result instanceof ApiError) {
        // Handler errors HTTP status code
        const { duration, data, status, ...response } = result?.['result'] || {};
        const errors = await ERRORS();
        const errorsByStatus = {
            [STATUS.UNAUTHORIZED]: errors.UNAUTHORIZED,
            [STATUS.NOTFOUND]: errors.NOTFOUND,
            [STATUS.CONFLICT]: errors.CONFLICT,
            default: errors.UNKNOWN,
        };

        await error({ ...response, data: { ...(errorsByStatus[status] || errorsByStatus.default), ...(data || {}) } });
    }
    return result;
};
